/**
 * The single API read boundary: parse a response against its zod schema,
 * observe what production actually sends, and return the best typed value we
 * can build (AMP-569, AMP-593).
 *
 * ## Why this observes instead of enforcing
 *
 * AMP-585 shipped this boundary in strict mode and it was rolled back in
 * production. Four static audit passes each found new schema/wire mismatches
 * and the find rate never converged, for two systematic reasons: the generated
 * Zod base is inaccurate (drf-spectacular types nullable fields non-null, so
 * derived schemas inherit wrong nullability by construction), and fixes were
 * not carried across sibling schemas. Static auditing cannot enumerate data
 * states — a deleted user, a photo attachment, a portfolio-less property — so
 * only production can tell us what is really on the wire. This boundary is the
 * instrument for collecting that: it reports and degrades, and a domain is
 * flipped to strict per `apiBoundaryConfig` once its evidence supports it.
 *
 * ## The three tiers
 *
 * 1. **Strict.** `schema.safeParse(data)`. Success returns the parsed value —
 *    the overwhelmingly common path, and unchanged from the strict boundary.
 * 2. **Relaxed.** On strict failure, reparse through {@link relaxNullability}:
 *    the same schema with nullability widened but every coercion, transform and
 *    refinement intact. Success returns that, and the mismatch is reported.
 * 3. **Raw.** Only if the relaxed pass also fails — meaning a genuine type
 *    error rather than a nullability gap — report at higher severity and return
 *    the unparsed body.
 *
 * ## Why tier 2 exists at all
 *
 * The naive report-only design is "on failure, return the raw data", and it is
 * a money bug. This helper is parse-and-adopt: it returns `z.infer<Schema>`, so
 * call sites are typed against the COERCED output and should carry no casts —
 * a lint rule (`local/no-parse-api-response-cast`, AMP-620) enforces that for
 * new code; a handful of pre-existing, individually-justified casts remain
 * (see that rule's disable comments) where the Zod-inferred type provably
 * diverges from a hand-written type it feeds. Handing back raw data on
 * failure gives a `number`-typed consumer
 * `proposedAmount: '1234.56'` — a string — producing NaN and string-concat
 * arithmetic, silently, and exactly on the responses we are hunting. Tier 2
 * keeps money coerced while tolerating the nullability gaps. Tier 3 is the
 * last resort where a typed value genuinely cannot be built; it is reported as
 * an error precisely because raw data reaching a consumer is not safe.
 *
 * This helper also absorbs the former `validateApiResponse`, which was the same
 * concept (safeParse + console.error) behind a default-off flag and returned
 * raw data. There is one boundary now, not two.
 */
import { z, ZodError } from 'zod';

import { isStrictKind } from './apiBoundaryConfig';
import { relaxNullability } from './relaxNullability';
import { logSchemaMismatch, reportSchemaMismatch } from './reportSchemaMismatch';

export interface ParseApiResponseOptions {
  /**
   * Force strict (throw on mismatch) regardless of the per-domain registry.
   * Production code should leave this unset and let `apiBoundaryConfig` decide;
   * it exists so tests can pin a schema's tightness independently of policy.
   */
  strict?: boolean;
}

type Attempt = { ok: true; value: unknown } | { ok: false; error: ZodError };

/**
 * `safeParse` only converts validation failures into a result; a transform or
 * refinement that throws on an input it never expected still propagates. That
 * risk is real on the relaxed path, where nulls reach transforms written for
 * present values — and this boundary must never throw in report-only mode.
 *
 * ⚠️ This catch is a backstop, NOT a free win, and it is worth understanding
 * before adding an object-level `.superRefine()` to a READ schema. A superRefine
 * that dereferences a field the relaxed pass just widened to null would throw
 * here; the relaxed attempt would then look like a failure, and the response
 * would fall to the raw tier — returning uncoerced decimal strings, the exact
 * outcome tier 2 exists to prevent. No read schema does this today (both
 * object-level superRefines in the app are on FORM schemas, which never reach
 * this boundary). Keep it that way: refinements that assume presence belong on
 * the write/form path, not the read path.
 */
function tryParse(schema: z.ZodTypeAny, data: unknown): Attempt {
  try {
    const result = schema.safeParse(data);
    return result.success ? { ok: true, value: result.data } : { ok: false, error: result.error };
  } catch (cause) {
    if (cause instanceof ZodError) {
      return { ok: false, error: cause };
    }
    return {
      ok: false,
      error: new ZodError([
        {
          code: z.ZodIssueCode.custom,
          path: [],
          message: `schema threw during parse: ${cause instanceof Error ? cause.message : String(cause)}`,
        },
      ]),
    };
  }
}

export function parseApiResponse<Schema extends z.ZodTypeAny>(
  schema: Schema,
  data: unknown,
  kind: string,
  options?: ParseApiResponseOptions,
): z.infer<Schema> {
  const strictAttempt = tryParse(schema, data);
  if (strictAttempt.ok) {
    return strictAttempt.value;
  }

  // Tier 1 failed. Under strict this is the pre-AMP-593 boundary verbatim: log
  // and throw, which an SWR fetcher surfaces as the hook's `error` state.
  if (options?.strict ?? isStrictKind(kind)) {
    logSchemaMismatch(kind, strictAttempt.error, 'strict', schema);
    throw new Error(`${kind} response did not match the expected schema`);
  }

  // Tier 2. Run the relaxed pass BEFORE reporting so the report can name the
  // outcome and pick its severity from what actually happened, rather than
  // filing every mismatch at the same level.
  const relaxedAttempt = tryParse(relaxNullability(schema), data);
  if (relaxedAttempt.ok) {
    reportSchemaMismatch(kind, schema, strictAttempt.error, 'relaxed');
    return relaxedAttempt.value;
  }

  // Tier 3. Not a nullability gap — a genuine type error. Reported as an error
  // because the value below is unparsed and uncoerced.
  //
  // Grouping is driven by the RELAXED error: the strict error also lists every
  // nullability gap the relaxed pass absorbed, which would both file those as
  // raw-tier errors and, against the per-response issue budget, crowd out the
  // genuine type error that actually forced this fallback. The strict error
  // rides along as context so the loudest tier still says what happened first.
  reportSchemaMismatch(kind, schema, relaxedAttempt.error, 'raw', strictAttempt.error);
  return data as z.infer<Schema>;
}
